import React from "react";

const SuicideIntentForm = ({ formData, handleChange }) => {
    const items = [
        { id: "isolation", title: "Isolation", options: ["Somebody present", "Somebody nearby, or in visual or vocal contact", "No one nearby or in visual or vocal contact"] },
        { id: "timing", title: "Timing", options: ["Intervention is probable", "Intervention is not likely", "Intervention is highly unlikely"] },
        { id: "precautions", title: "Precautions against discovery/intervention", options: ["No precautions", "Passive precautions (avoiding others but doing nothing to prevent intervention; alone in room with unlocked door)", "Active precautions (locked door)"] },
        { id: "actingToGetHelp", title: "Acting to Get Help During/After Attempt", options: ["Notified potential helper regarding attempt", "Contacted but did not specifically notify potential helper regarding attempt", "Did not contact or notify potential helper"] },
        { id: "finalActs", title: "Final Acts in Anticipation of Death (e.g., will, gifts, insurance)", options: ["None", "Thought about or made some arrangements", "Made definite plans or completed arrangements"] },
        { id: "activePreparation", title: "Active Preparation for Attempt", options: ["None", "Minimal to moderate", "Extensive"] },
        { id: "suicideNote", title: "Suicide Note", options: ["Absence of note", "Note written, but torn up; note thought about", "Presence of note"] },
        { id: "overtCommunication", title: "Overt Communication of Intent Before the Attempt", options: ["None", "Equivocal communication", "Unequivocal communication"] },
        { id: "allegedPurpose", title: "Alleged Purpose of Attempt", options: ["To manipulate environment, get attention, revenge", "Components of '0' and '2'", "To escape, surcease, solve problems"] },
        { id: "expectationsFatality", title: "Expectations of Fatality", options: ["Thought that death was unlikely", "Thought that death was possible but not probable", "Thought that death was probable or certain"] },
        { id: "conceptionLethality", title: "Conception of Method's Lethality", options: ["Did less to self than he thought would be lethal", "Wasn't sure if what he did would be lethal", "Equaled or exceeded what he thought would be lethal"] },
        { id: "seriousness", title: "Seriousness of Attempt", options: ["Did not seriously attempt to end life", "Uncertain about seriousness to end life", "Seriously attempted to end life"] },
        { id: "attitudeLivingDying", title: "Attitude Toward Living/Dying", options: ["Did not want to die", "Components of '0' and '2'", "Wanted to die"] },
        { id: "conceptionRescuability", title: "Conception of Medical Rescuability", options: ["Thought that death would be unlikely if he received medical attention", "Was uncertain whether death could be averted by medical attention", "Was certain of death even if he received medical attention"] },
        { id: "premeditation", title: "Degree of Premeditation", options: ["None; Impulsive", "Suicide contemplated for three hours or less prior to attempt", "Suicide contemplated for more than three hours prior to attempt"] },
        { id: "reactionAttempt", title: "Reaction to Attempt", options: ["Sorry about attempt; feels foolish, ashamed", "Accepts both attempt and its failure", "Regrets failure of attempt"] },
        { id: "visualizationDeath", title: "Visualization of Death", options: ["Life-after-death, reunion with decedents", "Never-ending sleep, darkness, end-of-things", "No conceptions of, or thoughts about death"] },
        { id: "previousAttempts", title: "Number of Previous Attempts", options: ["None", "One or two", "Three or more"] },
        { id: "alcoholIntakeAlcohol", title: "Relationship between Alcohol Intake and Attempt", options: ["Some alcohol intake prior to but not related to attempt, reportedly not enough to impair judgment", "Enough alcohol intake to impair judgment, reality testing and diminish responsibility/impulse control", "Intentional intake of alcohol in order to facilitate implementation of suicide attempt"] },
        { id: "intakeDrug", title: "Relationship between Drug Intake and Attempt", options: ["Some drug intake prior to but not related to attempt, reportedly not enough to impair judgment", "Enough drug intake to impair judgment, identity testing and diminish responsibility/impulse control", "Intentional drug intake in order to facilitate implementation of suicide attempt"] }
    ];

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (parseInt(formData[item.id] || 0, 10)), 0);
    };

    const totalScore = calculateTotal();

    const getRiskLevel = (score) => {
        if (score <= 10) return { text: "Low Risk", color: "success", recommendation: "Send home with advice to see Community Mental Health Team or GP." };
        if (score <= 19) return { text: "Medium Risk", color: "warning", recommendation: "Assessment by Community Mental Health Team or Psychiatrist advisable. Follow-up required. Admission optional if lives alone, previous attempts, or depressed." };
        return { text: "High Risk", color: "danger", recommendation: "Immediate psychiatric assessment. Admission recommended. Involuntary admission may be required." };
    };

    const risk = getRiskLevel(totalScore);

    return (
        <>
            <div className="row mb-4 position-sticky top-0 bg-white pt-3" style={{ zIndex: 10 }}>
                <div className="col-md-3">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        data-bs-toggle="modal"
                        data-bs-target="#sisGuidelines"
                    >
                        <i className="ri-information-line me-2"></i>
                        Guidelines & Risk Recommendations
                    </button>
                </div>
            </div>
            <div className="p-3" style={{ maxHeight: "60vh", overflowY: "auto", scrollbarWidth: "thin" }}>
                {/* Guidelines Button */}


                <div className="accordion" id="sisAccordion">
                    {/* Client Information */}
                    <div className="accordion-item mb-3 shadow-sm rounded">
                        <h2 className="accordion-header">
                            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#clientInfo">
                                Client Information
                            </button>
                        </h2>
                        <div id="clientInfo" className="accordion-collapse collapse show">
                            <div className="accordion-body pt-3">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="clientName"
                                                value={formData.clientName || ""}
                                                onChange={handleChange}
                                                readOnly={!!formData.patientId} // ⭐ ADD THIS PROPERTY ⭐
                                            />
                                            <label>Client Name</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="diagnosis" value={formData.diagnosis || ""} onChange={handleChange} />
                                            <label>Diagnosis</label>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name="assessedBy" value={formData.assessedBy || ""} onChange={handleChange} />
                                            <label>Assessed By</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Scale Items - Each collapsible */}
                    {items.map((item) => (
                        <div className="accordion-item mb-3 shadow-sm rounded" key={item.id}>
                            <h2 className="accordion-header">
                                <button
                                    className="accordion-button collapsed fw-medium text-dark"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#item_${item.id}`}
                                >
                                    <span className="me-3 fw-bold">{item.title}</span>
                                </button>
                            </h2>
                            <div id={`item_${item.id}`} className="accordion-collapse collapse">
                                <div className="accordion-body pt-4">
                                    <div className="row g-5">
                                        {item.options.map((opt, idx) => (
                                            <div className="col-lg-4" key={idx}>
                                                <div className="form-check mb-3">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={item.id}
                                                        id={`${item.id}_opt${idx}`}
                                                        value={idx}
                                                        checked={formData[item.id] === String(idx)}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium d-flex align-items-start" htmlFor={`${item.id}_opt${idx}`}>
                                                        <span className="text-primary fw-bold me-3 flex-shrink-0">{idx}</span>
                                                        <span>{opt}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Additional Comments */}
                    <div className="mt-4">
                        <label className="form-label fw-bold">Additional Comments</label>
                        <textarea
                            className="form-control"
                            rows="5"
                            name="additionalComments"
                            value={formData.additionalComments || ""}
                            onChange={handleChange}
                            placeholder="Enter any additional observations..."
                        />
                    </div>

                    {/* Clinician Info */}
                    <div className="row mt-5">
                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input type="text" className="form-control" name="clinicianName" value={formData.clinicianName || ""} onChange={handleChange} />
                                <label>Clinician’s full name</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Guidelines Modal */}
                <div className="modal fade" id="sisGuidelines" tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-body">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">Scoring</small>
                                <ul className="small">
                                    <li><strong>0–10</strong> → Low Risk</li>
                                    <li><strong>11–19</strong> → Medium Risk</li>
                                    <li><strong>20–30</strong> → High Risk</li>
                                </ul>

                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">Recommendations</small>
                                <p className="small">
                                    <strong>Low Risk:</strong> Send home with advice to see Community Mental Health Team or GP.<br />
                                    <strong>Medium Risk:</strong> Assessment by CMHT/Psychiatrist advisable. Follow-up required. Admission optional if lives alone, previous attempts, depressed.<br />
                                    <strong>High Risk:</strong> Immediate psychiatric assessment. Admission recommended. Involuntary admission may be required.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-primary" data-bs-dismiss="modal">Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SuicideIntentForm;